import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HelpCircle, Bluetooth, Music, Users, Trophy, Zap } from "lucide-react";

export const HowToPlay = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        data-testid="how-to-play-btn"
        onMouseEnter={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        className="inline-flex items-center gap-2 text-white font-bebas uppercase tracking-widest hover:opacity-90 transition-opacity cursor-pointer select-none"
        style={{ fontSize: "18pt", textShadow: "0 2px 6px rgba(0,0,0,0.55)" }}
        aria-label="Hoe te spelen"
      >
        <span
          className="blink-red-white flex items-center justify-center rounded-full border-2 shrink-0"
          style={{ width: "2em", height: "2em", fontSize: "0.85em" }}
        >
          <HelpCircle className="w-[1.2em] h-[1.2em]" strokeWidth={2.8} />
        </span>
        <span>Hoe te spelen</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="how-to-play-modal"
          onMouseLeave={() => setOpen(false)}
          className="max-w-2xl border-4 border-navy bg-cream text-navy p-0 overflow-hidden max-h-[90vh]"
        >
          <div className="overflow-y-auto max-h-[90vh] p-6 sm:p-8">
            <DialogHeader className="mb-4">
              <DialogTitle className="font-bebas tracking-wider text-4xl sm:text-5xl text-navy text-left">
                Hoe te spelen
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-5 font-poppins text-navy" style={{ fontWeight: 300, fontSize: "10pt", lineHeight: 1.55 }}>
              <section className="flex gap-3">
                <Users className="w-6 h-6 shrink-0 mt-0.5" style={{ color: "#e65b7c" }} />
                <div>
                  <div className="font-poppins" style={{ fontWeight: 700, fontSize: "12pt" }}>1. Kies je modus</div>
                  <p style={{ fontWeight: 300, fontSize: "10pt" }}>
                    <b style={{ fontWeight: 700 }}>Phone modus:</b> tot 8 spelers joinen via de room code of QR op hun telefoon.
                    <br />
                    <b style={{ fontWeight: 700 }}>Board Game modus:</b> alleen het hostscherm — spelers gebruiken het fysieke bordspel.
                  </p>
                </div>
              </section>

              <section className="flex gap-3">
                <Music className="w-6 h-6 shrink-0 mt-0.5" style={{ color: "#4a7cab" }} />
                <div>
                  <div className="font-poppins" style={{ fontWeight: 700, fontSize: "12pt" }}>2. Selecteer playlists & game types</div>
                  <p style={{ fontWeight: 300, fontSize: "10pt" }}>
                    Hier staan standaard een aantal playlists klaar. Wil je een andere? Verwijder er
                    eentje met het <b style={{ fontWeight: 700 }}>prullenbak icoon</b>, druk vervolgens op
                    <b style={{ fontWeight: 700 }}> + Add Playlist</b> en plak de YouTube URL erin.
                    Kies 1 tot 4 playlists (nummers worden willekeurig gepoold zonder herhaling)
                    en één of meerdere game types — rondes worden gerandomiseerd.
                  </p>
                </div>
              </section>

              <section className="flex gap-3">
                <Zap className="w-6 h-6 shrink-0 mt-0.5" style={{ color: "#cdbb44" }} />
                <div>
                  <div className="font-poppins" style={{ fontWeight: 700, fontSize: "12pt" }}>3. Spelen</div>
                  <p style={{ fontWeight: 300, fontSize: "10pt" }}>
                    <b style={{ fontWeight: 700 }}>Type 1:</b> raad de artiest, titel en het juiste jaar voor bonuspunten.
                    <br />
                    <b style={{ fontWeight: 700 }}>Type 2:</b> een 25-seconden fragment — de eerste die op de <span style={{ color: "#22c55e", fontWeight: 700 }}>groene knop</span> drukt mag antwoorden.
                    <br />
                    <b style={{ fontWeight: 700 }}>Type 3:</b> Photo Mosaic — 4×4 puzzelblokken van de artiestenfoto onthullen langzaam. Raad de artiest (+ album voor bonus).
                    <br />
                    <b style={{ fontWeight: 700 }}>Type 4:</b> videoclip speelt op je eigen telefoon. Wie het eerst buzzt raadt artiest, titel en kiest het juiste jaartal (3 opties).
                    <br />
                    Met de <span style={{ color: "#dc2626", fontWeight: 700 }}>rode challenge knop</span> kun je 1x per ronde het antwoord van een tegenstander uitdagen.
                  </p>
                </div>
              </section>

              <section className="flex gap-3">
                <Trophy className="w-6 h-6 shrink-0 mt-0.5" style={{ color: "#e65b7c" }} />
                <div>
                  <div className="font-poppins" style={{ fontWeight: 700, fontSize: "12pt" }}>4. Scores & winnaar</div>
                  <p style={{ fontWeight: 300, fontSize: "10pt" }}>
                    Na elke ronde onthult Hitmaster de juiste artiest, titel en releasejaar met de albumhoes.
                    Wie na alle rondes de meeste punten heeft wint.
                  </p>
                </div>
              </section>

              <section
                className="flex gap-3 p-4 rounded-xl border-2"
                style={{ borderColor: "#4a7cab", backgroundColor: "rgba(74,124,171,0.10)" }}
              >
                <Bluetooth className="w-6 h-6 shrink-0 mt-0.5" style={{ color: "#4a7cab" }} />
                <div>
                  <div className="font-poppins" style={{ fontWeight: 700, fontSize: "12pt" }}>Bluetooth speakers koppelen</div>
                  <p style={{ fontWeight: 300, fontSize: "10pt" }}>
                    Wil je het geluid via je eigen speakers laten spelen? Koppel je Bluetooth speaker
                    <b style={{ fontWeight: 700 }}> voordat</b> je het spel start:
                  </p>
                  <ol className="list-decimal ml-5 mt-2 space-y-1" style={{ fontWeight: 300, fontSize: "10pt" }}>
                    <li>Zet je Bluetooth speaker aan en in koppel-modus.</li>
                    <li>
                      Open de Bluetooth instellingen van je host apparaat (telefoon, tablet of laptop)
                      en verbind met de speaker.
                    </li>
                    <li>Zorg dat de speaker als audio-uitgang geselecteerd is.</li>
                    <li>Start het spel — Hitmaster speelt automatisch via je speaker.</li>
                  </ol>
                  <p className="mt-2 opacity-80" style={{ fontWeight: 300, fontSize: "10.5pt" }}>
                    Tip: houd het volume van je host apparaat op ~80% voor de beste balans.
                  </p>
                </div>
              </section>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HowToPlay;
